import React, { useMemo } from 'react';

import DrawerExtension from '../../../common2/drawer/DrawerExtension';
import DrawerHeader from '../../../common2/drawer/DrawerHeader';
import BasicSelect from '../../../common2/BasicSelect';
import DocumentsNavigation from './DocumentsNavigation';
import { useDocumentsNavigation } from './useDocumentsNavigation';
import DocumentsImageControls from './DocumentsImageControls';
import { useImageControls } from './useImageControls';
import DocumentsPanViewer from './DocumentsPanViewer';
import { UploaderFile } from '../../../common/Uploader';

import styles from './DocumentsViewer.scss';

type Props = {
  files: UploaderFile[];
};

function DocumentsViewer(props: Props) {
  const { files } = props;

  const { selectedFileIndex, selectedFile, selectFile, goToNextFile, goToPrevFile } = useDocumentsNavigation(files);
  const { zoom, rotate, zoomLevel, rotationLevel, panPosition, setPanPosition, handleImageLoad } = useImageControls(
    selectedFileIndex
  );

  const options = useMemo(() => files.map((file) => ({ value: file.id, label: file.name })), [files]);

  if (!files.length || !selectedFile || !selectedFile.response) {
    return null;
  }

  const url = selectedFile.response.body.publicUrl;
  const isImage = selectedFile.type?.startsWith('image/');

  return (
    <DrawerExtension>
      <DrawerHeader ariaLabel="Documents viewer">
        <div className={styles.header}>
          <BasicSelect
            name="documents"
            ariaLabel="Select document"
            isSearchable={false}
            noOptionTranslation
            options={options}
            value={selectedFile.id}
            onChange={selectFile}
          />
          {isImage && <DocumentsImageControls zoomLevel={zoomLevel} rotate={rotate} zoom={zoom} />}
          <DocumentsNavigation
            current={selectedFileIndex}
            total={files.length}
            onNextButtonClick={goToNextFile}
            onPrevButtonClick={goToPrevFile}
          />
        </div>
      </DrawerHeader>
      {isImage ? (
        <div className={styles.imagePreviewContainer}>
          <DocumentsPanViewer
            url={url}
            alt={selectedFile.name}
            zoomLevel={zoomLevel}
            rotation={rotationLevel}
            panPosition={panPosition}
            setPanPosition={setPanPosition}
            handleImageLoad={handleImageLoad}
          />
        </div>
      ) : (
        <iframe className={styles.pdfPreviewContainer} src={url} title={selectedFile.name} />
      )}
    </DrawerExtension>
  );
}

export default React.memo(DocumentsViewer);
